import { Block, Flex } from '@aloft/toolkit-sui';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Typography } from '../../summary/components/Typography.tsx';
import { LongLoaderProps } from './types.ts';

const LOADER_HEIGHT = '.5rem';
const WIDTH_TO_BREAKPOINTS = 0.8;
const WIDTH_TO_SLOW_LORIS = 1 - WIDTH_TO_BREAKPOINTS - 0.05;

export const LoadingFakeProgressBar = ({
  estimatedDurationSeconds,
  messageBreakpoints,
}: LongLoaderProps) => {
  const [progressWidth, setProgressWidth] = useState<number>(0);
  const [slowLorisWidth, setSlowLorisWidth] = useState<number>(0);
  const [displayMessage, setDisplayMessage] = useState<string>(
    messageBreakpoints[0]?.message ?? '',
  );

  const barWidthRef = useRef<number>(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const animationRequestIdRef = useRef<number>(0);
  const lastIncrementTimeRef = useRef<number>(0);
  const lastMessageIndexRef = useRef<number>(Infinity);

  useLayoutEffect(() => {
    const listener = () => {
      barWidthRef.current = progressBarRef.current?.getBoundingClientRect().width ?? 0;
    };

    listener();
    window.addEventListener('resize', listener);

    return () => window.removeEventListener('resize', listener);
  }, []);

  useEffect(() => {
    const start = Date.now();

    const animate = () => {
      const currentTime = Date.now();
      const interval = currentTime - start;
      const percentOfDuration = interval / (estimatedDurationSeconds * 1000);
      const validMessages = messageBreakpoints.filter(
        ({ breakpoint }) => breakpoint < percentOfDuration,
      );

      const lastMessage =
        validMessages.length > 0 ? validMessages[validMessages.length - 1] : undefined;
      const isNewMessage = lastMessage?.breakpoint !== lastMessageIndexRef.current;
      // const currentMessage = messageBreakpoints[currentMessageIndex];

      if (lastMessage && isNewMessage) {
        setDisplayMessage(lastMessage.message);
        setProgressWidth(lastMessage.breakpoint);
        lastMessageIndexRef.current = lastMessage.breakpoint;
        lastIncrementTimeRef.current = currentTime;
      }

      // bump progress a little bit no matter what
      setSlowLorisWidth(percentOfDuration);

      animationRequestIdRef.current = requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationRequestIdRef.current);
  }, [estimatedDurationSeconds, messageBreakpoints]);

  return (
    <Flex className="loading-screen-progress-bar">
      <Flex
        ref={progressBarRef}
        width="40%"
        height={LOADER_HEIGHT}
        borderRadius={LOADER_HEIGHT}
        border="1px solid strokeDefault"
        overflow="hidden"
      >
        <Block
          className="progress"
          flexGrow={0}
          flexShrink={0}
          width={`${barWidthRef.current * progressWidth * WIDTH_TO_BREAKPOINTS}px`}
          height={LOADER_HEIGHT}
          backgroundColor="surfaceAccent4"
        />
        <Block
          flexGrow={0}
          flexShrink={0}
          width={`${barWidthRef.current * slowLorisWidth * WIDTH_TO_SLOW_LORIS}px`}
          height={LOADER_HEIGHT}
          backgroundColor="surfaceAccent4"
        />
      </Flex>
      <Typography variant="body1">{displayMessage}</Typography>
    </Flex>
  );
};
